import { useEffect, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const MAX_BYTES = 8 * 1024 * 1024;

export default function Documents({ targetUserId, hideHeader }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState("file"); // "file" | "url"
  const [form, setForm] = useState({ title: "", url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const isOwn = !targetUserId || targetUserId === user?.id;
  const canEdit = isOwn && user?.role === "entrepreneur";

  const load = () => {
    const q = isOwn ? "" : `?user_id=${targetUserId}`;
    api.get(`/documents${q}`)
      .then((r) => setDocs(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { load(); }, [targetUserId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fileToB64 = (f) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const title = (form.title || "").trim();
      if (!title) throw new Error(t("documents.titleRequired"));
      const payload = { title };
      if (mode === "file") {
        if (!file) throw new Error(t("documents.fileRequired"));
        if (file.size > MAX_BYTES) throw new Error(t("documents.tooLarge"));
        if (file.size === 0) throw new Error(t("documents.fileEmpty"));
        payload.file_b64 = await fileToB64(file);
        payload.mime = file.type || "application/octet-stream";
        payload.filename = file.name;
      } else {
        const url = (form.url || "").trim();
        if (!url) throw new Error(t("documents.urlRequired"));
        payload.url = url;
      }
      await api.post("/documents", payload);
      setForm({ title: "", url: "" });
      setFile(null);
      setAdding(false);
      load();
    } catch (e2) {
      const status = e2.response?.status;
      if (status === 413) setErr(t("documents.tooLarge"));
      else setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("documents.confirmRemove"))) return;
    try { await api.delete(`/documents/${id}`); load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openDoc = async (d) => {
    if (d.kind === "url") {
      window.open(d.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const token = localStorage.getItem("tb_token");
      const res = await fetch(`${API_BASE}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = t("documents.downloadFailed");
        try {
          const payload = await res.json();
          detail = formatApiErrorDetail(payload?.detail) || detail;
        } catch { /* blob error */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || d.title || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message);
    }
  };

  const fmtSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const hostOf = (url) => {
    try { return new URL(url).hostname; } catch { return url || ""; }
  };

  if (!docs) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-page" data-testid="documents-section">
      {!hideHeader && (
        <h1 className="tb-h1" data-testid="documents-heading">{t("documents.title")}</h1>
      )}
      <p className="tb-muted" style={{ marginBottom: "1.5rem", maxWidth: 760 }}>
        {canEdit ? t("documents.subtitleOwn") : t("documents.subtitleViewer")}
      </p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }} data-testid="documents-error">{err}</div>}

      {canEdit && !adding && (
        <button className="tb-primary-btn" onClick={() => setAdding(true)} data-testid="documents-add" style={{ marginBottom: 16 }}>
          + {t("documents.add")}
        </button>
      )}

      {canEdit && adding && (
        <form onSubmit={submit} className="tb-block" style={{ marginBottom: 18 }} data-testid="documents-form">
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <button type="button" className={`tb-chip ${mode === "file" ? "active" : ""}`} onClick={() => setMode("file")} data-testid="mode-file">{t("documents.modeFile")}</button>
            <button type="button" className={`tb-chip ${mode === "url" ? "active" : ""}`} onClick={() => setMode("url")} data-testid="mode-url">{t("documents.modeUrl")}</button>
          </div>
          <div>
            <label>{t("documents.titleField")}</label>
            <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("documents.titlePlaceholder")} data-testid="doc-title" />
          </div>
          {mode === "url" ? (
            <div>
              <label>{t("documents.urlField")}</label>
              <input type="text" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="doc-url" />
            </div>
          ) : (
            <div>
              <label>{t("documents.fileField")}</label>
              <input type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" />
              <p className="tb-muted" style={{ fontSize: "0.78rem", margin: "4px 0 0" }}>{t("documents.fileHint")}</p>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="submit" className="tb-primary-btn" disabled={busy} data-testid="doc-submit">
              {busy ? t("auth.loading") : t("documents.save")}
            </button>
            <button type="button" className="tb-ghost-btn" onClick={() => { setAdding(false); setForm({ title: "", url: "" }); setFile(null); }}>
              {t("book.cancel")}
            </button>
          </div>
        </form>
      )}

      {docs.length === 0 ? (
        <p className="tb-muted" data-testid="documents-empty">{t("documents.empty")}</p>
      ) : (
        <div className="tb-card-grid">
          {docs.map((d) => (
            <div key={d.id} className="tb-block" data-testid={`doc-${d.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button onClick={() => openDoc(d)} style={{ background: "transparent", border: 0, color: "var(--accent)", fontWeight: 700, textAlign: "left", padding: 0, cursor: "pointer", fontSize: "1rem" }} data-testid={`open-${d.id}`}>
                    {d.title}
                  </button>
                  <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }}>
                    {d.kind === "url" ? (
                      <>🔗 {hostOf(d.url)}</>
                    ) : (
                      <>📎 {d.filename} · {fmtSize(d.size)}</>
                    )}
                  </div>
                  <div className="tb-muted" style={{ fontSize: "0.72rem", marginTop: 4 }} data-testid={`doc-meta-${d.id}`}>
                    {[d.created_by_name, d.created_at ? new Date(d.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : ""].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {canEdit && (
                  <button className="tb-funnel-del" onClick={() => remove(d.id)} aria-label="remove" data-testid={`remove-${d.id}`}>×</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
